import {
  BlockEntity,
  cellValueFromGQL,
  displayClusterType,
  displayDosage,
  displayPageType,
  formatDateBackend,
  gqlCellValueFromValue,
  isFullDate,
  parseBlockDate,
} from "Backend";
import { Block, Cell, CellType, CellValue, ClusterType, FieldCellValue, PageType } from "generated/graphql";
import { contains } from "helpers/coll";

interface GetFieldCellValuesProps {
  cells: Cell[];
  block: Block | undefined;
  entity: BlockEntity | undefined;
  pageType: PageType | undefined;
  clusterType: ClusterType | undefined;
  lensRuleDescription: string | undefined;
  lensRuleSubtype: string | undefined;
}

export default function getFieldCellValues(props: GetFieldCellValuesProps): FieldCellValue[] {
  return props.cells.map((c) => {
    let value: CellValue = { type: c.type };
    const ent = props.entity;
    const stage = ent?.cancerStage;
    const cancer = ent?.cancer;
    const cancerType = ent?.cancerType;
    const cancerSubtype = ent?.cancerSubtype;
    const cancerRecurrence = ent?.cancerRecurrence;
    const dosage = ent?.dosage ? displayDosage(ent?.dosage) : undefined;
    const validPrefill = (value: string) => {
      // Anything is valid that is not a select or multiselect
      if (c.type !== CellType.Select && c.type !== CellType.MultiSelect) {
        return true;
      }
      // Selects should be valid if they are in the select options
      if (c.selectOptions && contains(c.selectOptions, value)) {
        return true;
      }
      return false;
    };
    const tryPrefillText = (v: string | undefined) => {
      if (v !== undefined) {
        if (validPrefill(v)) {
          if (c.type === CellType.MultiSelect) {
            value.multiSelectValue = [v];
          } else {
            value.textValue = v;
          }
        }
      }
    };
    const tryPrefillNumber = (v: number | undefined) => {
      if (c.type === CellType.Float && v !== undefined) {
        value.floatValue = v;
      } else if (c.type === CellType.Integer && v !== undefined) {
        value.integerValue = v;
      }
    };
    if (c.type === CellType.Date && c.key === "primary_date" && props.block?.date && isFullDate(props.block?.date)) {
      value.dateValue = formatDateBackend(parseBlockDate(props.block.date));
    } else if (c.key === "name") {
      tryPrefillText(props.lensRuleDescription);
    } else if (c.key === "lens_subtype") {
      tryPrefillText(props.lensRuleSubtype);
    } else if (c.key === "record_type" && props.pageType) {
      tryPrefillText(displayPageType(props.pageType));
    } else if (c.key === "record_type" && props.clusterType) {
      tryPrefillText(displayClusterType(props.clusterType));
    } else if (c.key === "cancer_stage" && stage) {
      tryPrefillText(stage);
    } else if (c.key === "cancer" && cancer) {
      tryPrefillText(cancer);
    } else if (c.key === "cancer_type" && cancerType) {
      tryPrefillText(cancerType);
    } else if (c.key === "cancer_subtype" && cancerSubtype) {
      tryPrefillText(cancerSubtype);
    } else if (c.key === "cancer_recurrence" && cancerRecurrence !== undefined) {
      value.textValue = props.entity?.cancerRecurrence ? "Yes" : "No";
    } else if (c.key === "dosage" && dosage) {
      tryPrefillText(dosage);
    } else if (c.key === "med_quantity") {
      tryPrefillNumber(ent?.medQuantity);
    } else if (c.key === "med_times_per_day") {
      tryPrefillNumber(ent?.medTimesPerDay);
    } else if (c.key === "med_day_supply") {
      tryPrefillNumber(ent?.medDaySupply);
    } else if (c.key === "ndc_code") {
      tryPrefillText(ent?.ndcCode);
    } else if (c.default !== undefined) {
      value = gqlCellValueFromValue(c.type, cellValueFromGQL(c.default));
    }
    return {
      cellId: c.id,
      valueGroupIndex: 0,
      value: value,
    };
  });
}
